// item 1516 (perf) — the curated brand fonts (ALLOWED_FONTS, models/companyBrand) used to ALL
// ship eagerly from index.tsx: 8 families × 2 weights in the main CSS, when a tenant renders
// exactly one. Eager now = Roboto (stock default) + Albert Sans/Barlow (RNWY kit) + Courier
// Prime (ledger skin). The other four load on demand from their own chunks:
//   (a) BrandThemeProvider — when the tenant's brand.fontFamily resolves to one of them
//   (b) the Branding font picker (FontField) — so every option previews in its own face
// Each family loads once per page (the promise is cached); failures are swallowed — the
// Chakra theme already names a sans-serif fallback, so a lost chunk only costs the look.
const LAZY_FONT_LOADERS: Record<string, () => Promise<unknown>> = {
  Montserrat: () => Promise.all([
    import(/* webpackChunkName: "font-montserrat" */ '@fontsource/montserrat/400.css'),
    import(/* webpackChunkName: "font-montserrat" */ '@fontsource/montserrat/700.css'),
  ]),
  Raleway: () => Promise.all([
    import(/* webpackChunkName: "font-raleway" */ '@fontsource/raleway/400.css'),
    import(/* webpackChunkName: "font-raleway" */ '@fontsource/raleway/700.css'),
  ]),
  Lato: () => Promise.all([
    import(/* webpackChunkName: "font-lato" */ '@fontsource/lato/400.css'),
    import(/* webpackChunkName: "font-lato" */ '@fontsource/lato/700.css'),
  ]),
  Inter: () => Promise.all([
    import(/* webpackChunkName: "font-inter" */ '@fontsource/inter/400.css'),
    import(/* webpackChunkName: "font-inter" */ '@fontsource/inter/700.css'),
  ]),
};

export const LAZY_FONT_FAMILIES = Object.keys(LAZY_FONT_LOADERS);

const loaded = new Map<string, Promise<void>>();

/** Load one brand family's 400 + 700 faces if it is a lazy one; a no-op for the eager faces. */
export const loadBrandFont = (family: string | null | undefined): Promise<void> => {
  const name = (family ?? '').trim();
  const loader = LAZY_FONT_LOADERS[name];
  if (!loader) return Promise.resolve();
  let p = loaded.get(name);
  if (!p) {
    p = loader().then(() => undefined).catch(() => { loaded.delete(name); /* retry on the next ask */ });
    loaded.set(name, p);
  }
  return p;
};

/** The font picker: pull every lazy family so each radio previews in its own face. */
export const loadAllLazyBrandFonts = (): Promise<void> => Promise.all(
  LAZY_FONT_FAMILIES.map((f) => loadBrandFont(f)),
).then(() => undefined);
